/**
 * Document Studio — a JSON viewer/editor, parser and diff checker for editing
 * Firestore documents at any path.
 *
 * Top: open-by-path bar.
 * Left: searchable list of documents opened this session.
 * Right: Code/Tree editor with live JSON validation.
 * On Save: a git-style line diff must be confirmed to commit.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import CreateDocumentModal from "./components/CreateDocumentModal";
import DiffModal from "./components/DiffModal";
import DocumentList from "./components/DocumentList";
import EditorPane from "./components/EditorPane";
import PathBar from "./components/PathBar";
import { useDocumentStudio } from "./hooks/useDocumentStudio";

/** Parent collection of a document path, used to prefill the create dialog. */
const parentCollectionOf = (path: string | undefined): string =>
  path ? path.split("/").slice(0, -1).join("/") : "";

const DocumentStudio = () => {
  const studio = useDocumentStudio();
  const [showDiff, setShowDiff] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const handleConfirmSave = async () => {
    const result = await studio.commit();
    if (result.ok) {
      toast.success("Document saved");
      setShowDiff(false);
    } else {
      toast.error(result.error ?? "Could not save document");
    }
  };

  const handleCreate = async (collectionPath: string, docId: string) => {
    const result = await studio.newDocument(collectionPath, docId);
    if (result.ok) {
      toast.success(
        result.existed
          ? "Document already exists — opened it"
          : "Document created",
      );
      setShowCreate(false);
    }
    return result;
  };

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden">
      <div className="flex items-center justify-between border-b border-primaryGray-15 pb-2">
        <div className="text-h11 font-semibold">Document Studio</div>
        <div className="text-tiny text-primaryGray-8">
          Open/Add any document by path, edit it, then review the diff before
          saving.
        </div>
      </div>

      <PathBar
        onOpen={studio.openPath}
        onNew={() => setShowCreate(true)}
        onClearError={studio.clearError}
        loading={studio.loading}
        error={studio.error}
      />

      <div className="flex flex-1 gap-3 overflow-hidden">
        <DocumentList
          documents={studio.openedDocuments}
          selectedPath={studio.selected?.path ?? null}
          dirtyPath={studio.isDirty ? (studio.selected?.path ?? null) : null}
          onSelect={studio.selectDocument}
          onClose={studio.closeDocument}
        />

        <EditorPane
          document={studio.selected}
          text={studio.text}
          onTextChange={studio.setText}
          parsed={studio.parsed}
          isDirty={studio.isDirty}
          canSave={studio.canSave}
          loading={studio.loading}
          onFormat={studio.format}
          onReset={studio.reset}
          onSave={() => setShowDiff(true)}
        />
      </div>

      <DiffModal
        isOpen={
          showDiff && Boolean(studio.selected) && studio.parsed.value !== null
        }
        oldData={studio.selected?.data ?? {}}
        newData={studio.parsed.value ?? {}}
        saving={studio.saving}
        onCancel={() => setShowDiff(false)}
        onConfirm={handleConfirmSave}
      />

      <CreateDocumentModal
        isOpen={showCreate}
        defaultCollectionPath={parentCollectionOf(studio.selected?.path)}
        onCreate={handleCreate}
        onClose={() => setShowCreate(false)}
      />
    </div>
  );
};

export default DocumentStudio;
